import { fieldEntryHasPresentableValue } from '@/common/utils/changedDataFieldMerge'
import { parsePageFieldToDate } from '@/common/utils/parsePageFieldToDate'
import { setType } from '@/common/utils/typedValueSerializer'
import { toMultiSelectTypedValue } from '@/common/utils/normalizeMultiselectTableValue'

export function pageFieldNameCandidates(baseFieldName: string, multiSelect = false): string[] {
  if (multiSelect) return [baseFieldName]
  return [
    baseFieldName,
    `${baseFieldName}__listbox`,
    `${baseFieldName}__items`,
    `${baseFieldName}__action`,
  ]
}

export function resolvePageFieldEntry(
  pageFields: Record<string, any>,
  baseFieldName: string,
  multiSelect = false,
): unknown {
  for (const key of pageFieldNameCandidates(baseFieldName, multiSelect)) {
    if (key in pageFields) return pageFields[key]
  }
  return undefined
}

function typedRegistryFieldEqual(prev: unknown, next: unknown): boolean {
  if (prev === next) return true
  if (prev == null || next == null) return false
  if (typeof prev !== 'object' || typeof next !== 'object' || Array.isArray(prev) || Array.isArray(next)) {
    return false
  }
  const p = prev as { type?: string; value?: unknown }
  const n = next as { type?: string; value?: unknown }
  if (p.type !== n.type) return false
  if (p.value === n.value) return true
  const pd = parsePageFieldToDate(p)
  const nd = parsePageFieldToDate(n)
  if (pd && nd) return pd.getTime() === nd.getTime()
  return false
}

function readUniDataTableEmptyFlag(entry: unknown): boolean | undefined {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return undefined
  const typed = entry as { value?: { empty?: unknown }; empty?: unknown }
  const nested =
    typed.value && typeof typed.value === 'object' && !Array.isArray(typed.value)
      ? typed.value.empty
      : undefined
  if (typeof nested === 'boolean') return nested
  if (typeof typed.empty === 'boolean') return typed.empty
  return undefined
}

function isAuthoritativeEmptyTableField(entry: unknown): boolean {
  return readUniDataTableEmptyFlag(entry) === true
}

/** Записать одно поле из pageFields в слот forms registry (panel/element). */
export function assignPageFieldToRegistrySlot(
  registrySlot: Record<string, unknown>,
  baseFieldName: string,
  pageFields: Record<string, any>,
  options?: { multiSelect?: boolean },
): boolean {
  const fieldData = resolvePageFieldEntry(pageFields, baseFieldName, options?.multiSelect)
  if (fieldData === undefined) return false

  const existing = registrySlot[baseFieldName]
  if (
    isAuthoritativeEmptyTableField(existing) &&
    fieldEntryHasPresentableValue(fieldData) &&
    !isAuthoritativeEmptyTableField(fieldData)
  ) {
    return false
  }

  const authoritativeEmpty = isAuthoritativeEmptyTableField(fieldData)
  if (!authoritativeEmpty && !fieldEntryHasPresentableValue(fieldData)) {
    return false
  }

  const fieldValue =
    fieldData && typeof fieldData === 'object' && 'value' in fieldData
      ? (fieldData as { value: unknown }).value
      : fieldData

  const nextTyped = options?.multiSelect
    ? toMultiSelectTypedValue(fieldValue)
    : fieldData &&
        typeof fieldData === 'object' &&
        !Array.isArray(fieldData) &&
        'type' in fieldData &&
        'value' in fieldData
      ? (fieldData as { type: string; value: unknown })
      : setType(fieldValue)

  if (typedRegistryFieldEqual(existing, nextTyped)) {
    return false
  }

  registrySlot[baseFieldName] = nextTyped
  return true
}
